import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { setToken, API_BASE_URL } from '../services/api';
import './Login.css';

interface LoginResponse {
  success: boolean;
  userName: string;
  token: string;
  userId: number;
  errorMessage: string | null;
}

interface CreateUserResponse {
  userId: number;
  userName: string;
  createDate: string;
  active: boolean;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Registration modal state
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerFullName, setRegisterFullName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!username.trim()) {
      setError('Please enter your username');
      return;
    }
    
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }
    
    // Clear error if validation passes
    setError('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: username.trim(),
          password: password,
        }),
      });

      const data: LoginResponse = await response.json();

      if (data.success && data.token) {
        // Login successful - store JWT token, userId, and userName
        setToken(data.token);
        localStorage.setItem('userId', data.userId.toString());
        localStorage.setItem('userName', data.userName);
        
        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        // Login failed - show error message from API
        setError(data.errorMessage || 'Login failed. Please try again.');
      }
    } catch (err) {
      // Network error or other issues
      console.error('Login error:', err);
      setError('Unable to connect to server. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!registerUsername.trim()) {
      setRegisterError('Please enter a username');
      return;
    }
    
    if (!registerPassword.trim()) {
      setRegisterError('Please enter a password');
      return;
    }
    
    if (!registerFullName.trim()) {
      setRegisterError('Please enter your full name');
      return;
    }
    
    if (!registerEmail.trim()) {
      setRegisterError('Please enter your email');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerEmail.trim())) {
      setRegisterError('Please enter a valid email address');
      return;
    }
    
    // Clear errors
    setRegisterError('');
    setRegisterSuccess('');
    setIsRegisterLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: registerUsername.trim(),
          password: registerPassword,
          fullName: registerFullName.trim(),
          email: registerEmail.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: CreateUserResponse = await response.json();

      // Registration successful - ask user to login
      setRegisterSuccess(`Account created successfully! Please login with your credentials.`);
      setRegisterUsername('');
      setRegisterPassword('');
      setRegisterFullName('');
      setRegisterEmail('');
      
      // Close modal after 2 seconds and ask user to login
      setTimeout(() => {
        setShowRegisterModal(false);
        setRegisterSuccess('');
        // Don't navigate - user should login manually
      }, 2000);
    } catch (err) {
      // Network error or other issues
      console.error('Registration error:', err);
      setRegisterError('Unable to create account. Please try again.');
    } finally {
      setIsRegisterLoading(false);
    }
  };

  const closeModal = () => {
    setShowRegisterModal(false);
    setRegisterUsername('');
    setRegisterPassword('');
    setRegisterFullName('');
    setRegisterEmail('');
    setRegisterError('');
    setRegisterSuccess('');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <Logo size="large" showText={true} />
          <p className="login-subtitle">Sign in to manage your finances</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username
            </label>
            <input
              type="text"
              id="username"
              className="form-input"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setError(''); // Clear error when user types
              }}
              autoComplete="username"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              className="form-input"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(''); // Clear error when user types
              }}
              autoComplete="current-password"
            />
          </div>
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
          
          <div className="register-link-container">
            <p className="register-text">
              Don't have an account?{' '}
              <button
                type="button"
                className="register-link"
                onClick={() => setShowRegisterModal(true)}
              >
                Create New User
              </button>
            </p>
          </div>
        </form>
      </div>

      {/* Registration Modal */}
      {showRegisterModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create New Account</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            
            <form onSubmit={handleRegisterSubmit} className="login-form">
              {registerError && <div className="error-message">{registerError}</div>}
              {registerSuccess && <div className="success-message">{registerSuccess}</div>}
              
              <div className="form-group">
                <label htmlFor="register-fullname" className="form-label">
                  Full Name
                </label>
                <input
                  type="text"
                  id="register-fullname"
                  className="form-input"
                  placeholder="Enter your full name"
                  value={registerFullName}
                  onChange={(e) => {
                    setRegisterFullName(e.target.value);
                    setRegisterError(''); // Clear error when user types
                  }}
                  autoComplete="name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="register-email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="register-email"
                  className="form-input"
                  placeholder="Enter your email"
                  value={registerEmail}
                  onChange={(e) => {
                    setRegisterEmail(e.target.value);
                    setRegisterError(''); // Clear error when user types
                  }}
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="register-username" className="form-label">
                  Username
                </label>
                <input
                  type="text"
                  id="register-username"
                  className="form-input"
                  placeholder="Enter your username"
                  value={registerUsername}
                  onChange={(e) => {
                    setRegisterUsername(e.target.value);
                    setRegisterError(''); // Clear error when user types
                  }}
                  autoComplete="username"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="register-password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="register-password"
                  className="form-input"
                  placeholder="Enter your password"
                  value={registerPassword}
                  onChange={(e) => {
                    setRegisterPassword(e.target.value);
                    setRegisterError(''); // Clear error when user types
                  }}
                  autoComplete="new-password"
                />
              </div>
              
              
              
              <button 
                type="submit" 
                className="submit-button"
                disabled={isRegisterLoading}
              >
                {isRegisterLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;

